import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ValidationError } from 'yup';
import { BiX } from 'react-icons/bi';
import { Form } from '@unform/web';
import { FormHandles, SubmitHandler } from '@unform/core';

import Details, { DetailsFormSchema } from './steps/Details';
import ComplementItems, {
  ComplementItemsFormSchema,
} from './steps/ComplementItems';

import Tab from '../../../../newComponents/Tab';
import TabBar from '../../../../newComponents/TabBar';
import Button from '../../../../newComponents/Button';

import { useComplements, Complement } from '../../../../hooks/complements';
import { useToast } from '../../../../hooks/toast';

import APIError from '../../../../errors/APIError';

import getValidationErrors from '../../../../utils/getValidationErrors';

import { Container, TabPanel } from './styles';

type ComplementsProps = {
  currentTab: 0 | 1;
  isDrawerOpened?: boolean;
  lastComplementIndex: number;
  handleCloseDrawer(): void;
  handleChangeTab(tab: 0 | 1): void;
  onSave(complement: Complement): void;
};

const LAST_TAB_INDEX = 1;

const SCHEMAS = {
  0: DetailsFormSchema,
  1: ComplementItemsFormSchema,
};

const Complements: React.FC<ComplementsProps> = ({
  currentTab,
  isDrawerOpened,
  lastComplementIndex,
  onSave,
  handleChangeTab,
  handleCloseDrawer,
}) => {
  const formRef = useRef<FormHandles>(null);

  const {
    id,
    name,
    items,
    order,
    isRequired,
    maximumQuantity,
    minimumQuantity,
    handleResetComplement,
    handleAddComplementItem,
  } = useComplements();
  const { addToast } = useToast();

  const [isLoading, setIsLoading] = useState(false);

  const handleCancel = useCallback(() => {
    handleCloseDrawer();

    setTimeout(() => {
      handleChangeTab(0);
      handleResetComplement();
      formRef.current?.reset();
      formRef.current?.setErrors({});
    }, 500);
  }, [handleChangeTab, handleCloseDrawer, handleResetComplement]);

  useEffect(() => {
    formRef.current?.setFieldValue('name', name);

    if (isDrawerOpened && (!items || items?.length === 0)) {
      handleAddComplementItem();
    }
  }, [name, items, isDrawerOpened, handleAddComplementItem]);

  const handleSubmit: SubmitHandler<any> = useCallback(
    async (data) => {
      setIsLoading(true);

      formRef.current?.setErrors({});

      try {
        const currentTabSchema = SCHEMAS[currentTab];

        await currentTabSchema.validate(data, {
          abortEarly: false,
        });

        if (currentTab === 0) {
          handleChangeTab(1);
        } else {
          await Promise.all(
            Object.values(SCHEMAS).map(async (schema) => {
              await schema.validate(data, {
                abortEarly: false,
              });
            }),
          );

          const complementItems = items?.map((item, itemIndex) => ({
            ...item,
            ...data.items[itemIndex],
          }));

          onSave({
            id,
            name: data.name,
            order: order ?? lastComplementIndex,
            isRequired,
            maximumQuantity,
            minimumQuantity,
            items: complementItems,
          });
          handleCancel();
        }
      } catch (error) {
        let errors = {};

        if (error instanceof ValidationError) {
          errors = getValidationErrors(error);

          const detailsTabFields = ['name'];

          if (
            Object.keys(errors).some((field) =>
              detailsTabFields.includes(field),
            )
          ) {
            handleChangeTab(0);
          }

          formRef.current?.setErrors(errors);
        }

        if (error instanceof APIError) {
          addToast({
            title: error.message,
            type: 'error',
          });
        }
      } finally {
        setIsLoading(false);
      }
    },
    [
      id,
      items,
      order,
      currentTab,
      isRequired,
      maximumQuantity,
      minimumQuantity,
      lastComplementIndex,
      onSave,
      addToast,
      handleCancel,
      handleChangeTab,
    ],
  );

  return (
    <Container
      isVisible={isDrawerOpened}
      animated={false}
      containerStyle={{ zIndex: 99999990 }}
    >
      <Form ref={formRef} onSubmit={handleSubmit} initialData={{ name, items }}>
        <header>
          <div className="drawer-title">
            <h1>Complementos</h1>
            <button type="button" onClick={handleCancel} disabled={isLoading}>
              <BiX />
            </button>
          </div>
          <TabBar
            selectedTab={currentTab}
            onChange={(_event, value) => handleChangeTab(value)}
          >
            <Tab wrapped style={{ fontSize: '14px' }} >Detalhes</Tab >
            <Tab wrapped style={{ fontSize: '14px' }} >Complementos</Tab>
          </TabBar>
        </header>
        <main>
          <TabPanel index={0} selectedTab={currentTab}>
            <Details />
          </TabPanel>
          <TabPanel index={1} selectedTab={currentTab}>
            <ComplementItems />
          </TabPanel>
        </main>
        <footer>
          <Button
            type="button"
            variant="outlined"
            disabled={isLoading}
            onClick={handleCancel}
          >
            Cancelar
          </Button>
          <Button type="submit" loading={isLoading}>
            {currentTab === LAST_TAB_INDEX ? 'Salvar' : 'Continuar'}
          </Button>
        </footer>
      </Form>
    </Container>
  );
};

export default Complements;
